/* Cars4Part homepage sections: product slider tabs/arrows/progress, delivery estimate dates, simple scrollers. */

/* ---------- Delivery estimate ("Shipping arrives Wed, Oct 14") ---------- */
(() => {
  if (window.c4pEta) return;

  const formatter = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const cache = {};

  const arrivalDate = (days, skipWeekends) => {
    const key = `${days}-${skipWeekends}`;
    if (cache[key]) return cache[key];

    const date = new Date();
    let left = days;
    while (left > 0) {
      date.setDate(date.getDate() + 1);
      const day = date.getDay();
      if (skipWeekends && (day === 0 || day === 6)) continue;
      left -= 1;
    }
    cache[key] = formatter.format(date);
    return cache[key];
  };

  window.c4pEta = (root = document) => {
    root.querySelectorAll('[data-c4p-eta]').forEach((el) => {
      const days = parseInt(el.getAttribute('data-c4p-eta'), 10);
      if (!days) return;
      const target = el.querySelector('[data-c4p-eta-date]');
      if (target) target.textContent = arrivalDate(days, el.getAttribute('data-skip-weekends') === 'true');
      el.hidden = false;
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => window.c4pEta());
  } else {
    window.c4pEta();
  }

  document.addEventListener('shopify:section:load', (event) => window.c4pEta(event.target));
})();

/* ---------- Horizontal scroller helper (arrows + progress) ---------- */
(() => {
  if (window.customElements.get('c4p-product-slider')) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class C4pScroller extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const signal = this.abort.signal;

      this.prev = this.querySelector('[data-c4p-prev]');
      this.next = this.querySelector('[data-c4p-next]');
      this.progress = this.querySelector('[data-c4p-progress]');

      this.prev?.addEventListener('click', () => this.go(-1), { signal });
      this.next?.addEventListener('click', () => this.go(1), { signal });

      this.onScroll = () => {
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(() => {
          this.ticking = false;
          this.update();
        });
      };

      this.resizeObserver = new ResizeObserver(() => this.update());
      this.setup(signal);
      this.bindTrack();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.resizeObserver?.disconnect();
    }

    setup() {}

    get track() {
      return Array.from(this.querySelectorAll('[data-c4p-track]')).find((track) => !track.closest('[hidden]'));
    }

    bindTrack() {
      if (this.boundTrack) {
        this.boundTrack.removeEventListener('scroll', this.onScroll);
        this.resizeObserver.unobserve(this.boundTrack);
      }
      this.boundTrack = this.track;
      if (!this.boundTrack) return;
      this.boundTrack.addEventListener('scroll', this.onScroll, { passive: true });
      this.resizeObserver.observe(this.boundTrack);
      this.update();
    }

    go(direction) {
      const track = this.boundTrack;
      if (!track) return;
      const slide = track.firstElementChild;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const step = slide ? slide.getBoundingClientRect().width + gap : track.clientWidth;
      const perPage = Math.max(1, Math.floor((track.clientWidth + gap) / step));
      track.scrollBy({ left: direction * step * perPage, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }

    update() {
      const track = this.boundTrack;
      if (!track) return;
      const max = track.scrollWidth - track.clientWidth;
      const x = Math.abs(track.scrollLeft);

      if (this.prev) this.prev.disabled = x <= 2;
      if (this.next) this.next.disabled = x >= max - 2;
      this.classList.toggle('is-static', max <= 2);

      if (this.progress) {
        const visible = track.scrollWidth ? track.clientWidth / track.scrollWidth : 1;
        const width = Math.min(100, Math.max(12, visible * 100));
        const travel = max > 0 ? (x / max) * (100 - width) : 0;
        this.progress.style.width = `${width}%`;
        this.progress.style.transform = `translateX(${(travel / width) * 100}%)`;
      }
    }
  }

  /* ---------- Product slider with optional tabs ---------- */
  class C4pProductSlider extends C4pScroller {
    setup(signal) {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.viewAll = this.querySelector('[data-c4p-view-all]');

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.select(tab), { signal });
        tab.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
          event.preventDefault();
          const index = this.tabs.indexOf(tab) + (event.key === 'ArrowRight' ? 1 : -1);
          const next = this.tabs[(index + this.tabs.length) % this.tabs.length];
          next.focus();
          this.select(next);
        }, { signal });
      });

      // Theme editor: show the tab of the selected block
      document.addEventListener('shopify:block:select', (event) => {
        const tab = this.tabs.find((item) => item === event.target);
        if (tab) this.select(tab, false);
      }, { signal });
    }

    select(tab, animate = true) {
      if (tab.getAttribute('aria-selected') === 'true') return;

      this.tabs.forEach((item) => {
        const selected = item === tab;
        item.setAttribute('aria-selected', selected ? 'true' : 'false');
        const panel = document.getElementById(item.getAttribute('aria-controls'));
        if (!panel) return;
        panel.hidden = !selected;

        if (selected) {
          const track = panel.querySelector('[data-c4p-track]');
          if (track) track.scrollLeft = 0;
          if (animate && !reducedMotion.matches) {
            Array.from(panel.querySelectorAll('.c4p-ps__slide')).slice(0, 8).forEach((slide, i) => slide.style.setProperty('--c4p-i', i));
            panel.classList.remove('is-entering');
            void panel.offsetWidth;
            panel.classList.add('is-entering');
            setTimeout(() => panel.classList.remove('is-entering'), 900);
          }
        }
      });

      tab.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      if (this.viewAll && tab.dataset.url) this.viewAll.href = tab.dataset.url;
      this.bindTrack();
    }
  }

  window.customElements.define('c4p-product-slider', C4pProductSlider);
  window.customElements.define('c4p-scroller', class extends C4pScroller {});
})();

/* ---------- About text "Read more" (mobile) ---------- */
(() => {
  if (window.c4pAboutReady) return;
  window.c4pAboutReady = true;

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-c4p-about-toggle]');
    if (!button) return;
    const grid = button.previousElementSibling;
    const open = button.getAttribute('aria-expanded') !== 'true';
    button.setAttribute('aria-expanded', open ? 'true' : 'false');
    grid?.classList.toggle('is-open', open);
  });
})();
